import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Users, Zap, MessageSquare, Sparkles, TrendingUp, Clock,
  AlertTriangle, Lightbulb, HelpCircle, Eye, Plus, Loader2,
} from 'lucide-react';
import CoPSignalCard from '@/components/cop/CoPSignalCard';
import CoPMetricsCard from '@/components/cop/CoPMetricsCard';
import SynthesisDraftsCard from '@/components/cop/SynthesisDraftsCard';
import DiffusionInboxCard from '@/components/cop/DiffusionInboxCard';
import DecayReviewCard from '@/components/cop/DecayReviewCard';
import RadarPromptCard from '@/components/cop/RadarPromptCard';
import ContributionTokensBadge from '@/components/cop/ContributionTokensBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAFF_ROLES = new Set(['admin', 'manager', 'hr']);
const SIGNAL_TYPE_LABELS = {
  problem: { label: 'Problem', icon: AlertTriangle, color: '#FF3B30' },
  question: { label: 'Question', icon: HelpCircle, color: '#007AFF' },
  observation: { label: 'Observation', icon: Eye, color: '#8B5CF6' },
  idea: { label: 'Idea', icon: Lightbulb, color: '#FF9500' },
};

export default function CoPPage() {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const isStaff = STAFF_ROLES.has(user?.role);

  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [captureOpen, setCaptureOpen] = useState(false);

  useEffect(() => { loadSignals(); /* eslint-disable-next-line */ }, [filter]);

  const loadSignals = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === 'new') params.status = 'new';
      else if (filter === 'resonant') params.status = 'resonant';
      else if (filter === 'mine') params.mine = true;
      const res = await axios.get(`${API}/cop/signals`, { headers, params });
      setSignals(res.data.signals || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load signals');
    } finally { setLoading(false); }
  };

  const onSignalChanged = (updated) => {
    setSignals(prev => prev.map(s => s.signal_id === updated.signal_id ? updated : s));
  };

  const onSignalCreated = (created) => {
    setSignals(prev => [created, ...prev]);
    setCaptureOpen(false);
  };

  const counts = {
    all: signals.length,
    new: signals.filter(s => s.status === 'new').length,
    resonant: signals.filter(s => s.status === 'resonant' || s.status === 'synthesised').length,
  };

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="cop-page">
      {/* Hero */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A] flex items-center gap-2">
            <Users className="w-6 h-6 text-[#002FA7]" />
            Community of Practice
          </h1>
          <p className="text-sm text-[#4B5563] mt-1 max-w-2xl">
            The CoP Vitality Loop · capture frictions, signal relevance with one tap, surface
            recurring patterns. Stage 1 (Sensing) + Stage 2 (Validation) live — synthesis and
            diffusion coming next.
          </p>
        </div>
        <Dialog open={captureOpen} onOpenChange={setCaptureOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="capture-friction-btn">
              <Plus className="w-4 h-4 mr-1.5" />Capture friction
            </Button>
          </DialogTrigger>
          <CaptureFrictionDialog
            headers={headers}
            onCreated={onSignalCreated}
          />
        </Dialog>
      </div>

      {/* Contribution tokens badge (Stage 5) */}
      <div className="flex justify-end -mt-2">
        <ContributionTokensBadge headers={headers} />
      </div>

      {/* Smart Inbox — Stage 4 Adaptive Diffusion (visible to all users) */}
      <DiffusionInboxCard headers={headers} />

      {/* Ambient Problem Resonance Radar — visible to all users (feature is opt-in) */}
      <RadarPromptCard headers={headers} />

      {/* Admin metrics */}
      {isStaff && <CoPMetricsCard headers={headers} />}

      {/* Stage 6 — decay review (staff only, auto-hides when empty) */}
      {isStaff && <DecayReviewCard headers={headers} />}

      {/* Stage 3 synthesis drafts awaiting steward review (auto-hides if none) */}
      {isStaff && <SynthesisDraftsCard headers={headers} />}

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap" data-testid="cop-filters">
        {[
          { key: 'all', label: 'All' },
          { key: 'new', label: 'New' },
          { key: 'resonant', label: 'Resonant' },
          { key: 'mine', label: 'Mine' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`filter-${f.key}`}
            className={`text-[11px] px-3 py-1.5 rounded-full font-medium transition-colors ${
              filter === f.key
                ? 'bg-[#002FA7] text-white'
                : 'bg-[#F3F4F6] text-[#4B5563] hover:bg-[#E5E7EB]'
            }`}
          >
            {f.label}
            {counts[f.key] !== undefined && (
              <span className={`ml-1.5 text-[10px] ${filter === f.key ? 'text-white/80' : 'text-[#9CA3AF]'}`}>
                · {counts[f.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Signal list */}
      {loading ? (
        <div className="p-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#9CA3AF]" />
        </div>
      ) : signals.length === 0 ? (
        <Card className="border-dashed border-[#E5E7EB]" data-testid="cop-empty-state">
          <CardContent className="p-8 text-center">
            <Sparkles className="w-10 h-10 mx-auto text-[#9CA3AF] mb-3" />
            <h3 className="text-sm font-semibold text-[#0A0A0A] mb-1">No signals captured yet</h3>
            <p className="text-xs text-[#4B5563] max-w-md mx-auto mb-4">
              Capture a friction — an unsolved problem, a recurring question, or an "off" intuition. Your
              CoP peers can signal resonance with a single tap.
            </p>
            <Button
              onClick={() => setCaptureOpen(true)}
              className="bg-[#002FA7] hover:bg-[#00227A]"
              data-testid="cop-empty-cta"
            >
              <Plus className="w-4 h-4 mr-1.5" />Capture your first friction
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="cop-signals-list">
          {signals.map(sig => (
            <CoPSignalCard
              key={sig.signal_id}
              signal={sig}
              currentUserId={user?.user_id}
              isStaff={isStaff}
              headers={headers}
              typeLabels={SIGNAL_TYPE_LABELS}
              onChanged={onSignalChanged}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Capture dialog (inline, keeps the file coherent) ----------

function CaptureFrictionDialog({ headers, onCreated }) {
  const [description, setDescription] = useState('');
  const [signalType, setSignalType] = useState('auto');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const desc = description.trim();
    if (desc.length < 3) { toast.error('Describe the friction (min 3 chars)'); return; }
    setSubmitting(true);
    try {
      const payload = { description: desc };
      if (signalType !== 'auto') payload.signal_type = signalType;
      const res = await axios.post(`${API}/cop/signals`, payload, { headers });
      toast.success(`Signal captured · type: ${res.data.signal_type}${
        res.data.similar_signals?.length ? ` · ${res.data.similar_signals.length} similar found` : ''
      }`);
      setDescription(''); setSignalType('auto');
      onCreated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Capture failed');
    } finally { setSubmitting(false); }
  };

  return (
    <DialogContent className="max-w-lg" data-testid="capture-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-[#002FA7]" />Capture a friction
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-medium">
            What are you seeing?
          </label>
          <Textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="An unsolved problem, a recurring question, or something that feels off…"
            rows={4}
            className="mt-1"
            data-testid="friction-description-input"
            maxLength={2000}
          />
          <p className="text-[10px] text-[#9CA3AF] mt-1">
            AI will auto-classify and tag this. {description.length}/2000
          </p>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-medium">
            Type (optional override)
          </label>
          <Select value={signalType} onValueChange={setSignalType}>
            <SelectTrigger className="mt-1 h-9 text-xs" data-testid="friction-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto-classify (recommended)</SelectItem>
              <SelectItem value="problem">Problem</SelectItem>
              <SelectItem value="question">Question</SelectItem>
              <SelectItem value="observation">Observation</SelectItem>
              <SelectItem value="idea">Idea</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={submit}
          disabled={submitting || description.trim().length < 3}
          className="w-full bg-[#002FA7] hover:bg-[#00227A]"
          data-testid="submit-friction-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Zap className="w-4 h-4 mr-1.5" />}
          Capture signal
        </Button>
      </div>
    </DialogContent>
  );
}
